"use client";
import { useAtom } from "jotai";
import "react-responsive-modal/styles.css";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, useI18N } from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import { drawerMenuOpenAtom } from "@/store/atoms";
import HamburgerMenu from "../profile/HamburgerMenu";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  venueDetailsOpenedAtom,
} from "@/state/atoms";
import { bucketUrlPrefix, showLoginByDefault } from "common/config";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import { actionToPerformAfterLoginAtom } from "@/store/atoms";
import { useEffect, useState } from "react";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

function Navbar() {
  const i18n = useI18N();
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const { setSelectedVenue } = useSelectedVenue();
  const isDark = useDarkMode();
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const { showModal } = useAuthModals();

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );

  useEffect(() => {
    console.log("check actionToPerformAfterLogin ");
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "redirectToMySavePlace") {
        //redirect to home page
        navigate({ to: "/mysavedplaces" });
        setActionToPerformAfterLogin("");
      }
    }
  }, [actionToPerformAfterLogin, user, isLoggedIn]);


  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("isLoggedIn", isLoggedIn, user);
      if (showLoginByDefault === "Yes" && !isLoggedIn && !user) {
        showModal(AuthModalScreen.EnterEmail);
      }
    }, 1000);

    return () => clearTimeout(timer); // Cleanup the timeout on unmount
  }, [showLoginByDefault, isLoggedIn, user]); // Add isLoggedIn to the dependency array

 
  const handleOpenAuthModal = () => {
    showModal(AuthModalScreen.EnterEmail);
  };

  const matchRoute = useMatchRoute();

  // Determine if the current route matches '/'
  const isExploreActive =
    matchRoute({ to: "/" }) ||
    matchRoute({ to: `/venuedetails/$venueId` }) || 
    matchRoute({ to: `/venuephotos/$venueSlug` }) ||
    matchRoute({ to: `/venuereviews/$venueId` });

  // Determine if the current route matches '/mysavedplaces'
  const isMySavedPlacesActive =
    matchRoute({ to: "/mysavedplaces" }) ||
    matchRoute({ to: `/savedplacedetails/$savedPlaceId/user/$userId` });

  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);

  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;

  const [, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);

  const toggleDrawerMenu = () => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  };

  const clearsetSelectedVenue = () => {
    AnalyticsService.trackGAEventHandler("navigatedToMapView");
    AnalyticsService.trackMx("navigatedToMapView", {});

    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    navigate({ to: "/" });
  };

  const clearsetSelectedVenueMsp = () => {
    AnalyticsService.trackGAEventHandler("navigatedToMySavedPlaces");
    AnalyticsService.trackMx("navigatedToMySavedPlaces", {});

    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    navigate({ to: "/mysavedplaces" });
  };

  const [isMSPHovered, setIsMSPHovered] = useState(false); // State to track hover
  const [isExploreHovered, setIsExploreHovered] = useState(false); // Hover state

  return (
    <div className="md:h-[60px] h-[60px] bg-white dark:bg-primary-900 pt-[5px] md:pt-[0px]">
      <header className="h-full" role="none">
        <nav
          className="pl-4 md:pl-8 pr-4 flex items-center justify-start"
          role="none"
        >
          <div
            className="cursor-pointer"
            onClick={clearsetSelectedVenue}
            aria-label="Home Link"
            tabIndex={0}
          >
            {isDark ? (
              <img
                className="md:h-auto max-h-[58px]"
                src="/images/Logo_dark.svg"
                alt="Sociability Logo"
                aria-hidden="true"
              />
            ) : (
              <img
                className="md:h-auto max-h-[58px]"
                src="/images/Logo.svg"
                alt="Sociability Logo"
                aria-hidden="true"
              />
            )}
          </div>
          <div className="flex-grow"></div>

          <ul className="hidden md:flex gap-6" role="none">
            {/* Explore Button */}
            <li role="none">
              <div
                className={`cursor-pointer px-3 py-1 flex items-center gap-1 relative ${isExploreActive ? "active" : ""}`}
                aria-pressed={isExploreActive ? "true" : "false"}
                onMouseEnter={() => setIsExploreHovered(true)} // Set hover to true
                onMouseLeave={() => setIsExploreHovered(false)} // Set hover to false
                onClick={clearsetSelectedVenue}
                aria-label={
                  isExploreActive
                    ? "Explore Venues Link Selected"
                    : "Explore Venues Link"
                }
                tabIndex={0}
                role="link"
              >
                <span className="w-6 h-6 relative">
                  {isDark ? (
                    <img
                      src={
                        isExploreActive || isExploreHovered
                          ? "/images/MagnifyingGlass_dark_default.svg"
                          : "/images/MagnifyingGlass_white.svg"
                      }
                      alt="Explore Icon"
                      aria-hidden="true"
                    />
                  ) : (
                    <img
                      src={
                        isExploreActive || isExploreHovered
                          ? "/images/MagnifyingGlass_active.svg"
                          : "/images/MagnifyingGlass_default.svg"
                      }
                      alt="Explore Icon"
                      aria-hidden="true"
                    />
                  )}
                </span>

                {isDark ? (
                  <span
                    aria-hidden="true"
                    className={`button-small-medium relative ${isExploreActive || isExploreHovered ? "text-primary-300" : "text-white"}`}
                  >
                    {i18n.get("explore")}
                    {/* Render the div on hover */}
                    {isExploreHovered && (
                      <div
                        style={{
                          width: "100%", // Ensure the hover effect takes the full width of the text
                          height: 0,
                          borderBottom: "3px solid #A39FE8", // 3px border for light theme
                          position: "absolute", // Ensure the border is positioned without moving the text
                          bottom: -8, // Adjust position right under the text
                          left: 0, // Align with the text
                          marginTop: "4px",
                        }}
                      ></div>
                    )}
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    className={`button-small-medium relative ${isExploreActive || isExploreHovered ? "text-primary-500" : "text-neutral-700"}`}
                  >
                    {i18n.get("explore")}
                    {/* Render the div on hover */}
                    {isExploreHovered && (
                      <div
                        style={{
                          width: "100%", // Ensure the hover effect takes the full width of the text
                          height: 0,
                          borderBottom: "3px solid #352FC3", // 3px border for dark theme
                          position: "absolute", // Ensure the border is positioned without moving the text
                          bottom: -8, // Adjust position right under the text
                          left: 0, // Align with the text
                          marginTop: "4px",
                        }}
                      ></div>
                    )}
                  </span>
                )}
              </div>
            </li>

            {/* My Saved Places Button */}
            <li role="none">
              <div
                className={`px-3 py-1 flex items-center gap-1 relative ${isMySavedPlacesActive ? "" : ""} cursor-pointer`}
                aria-pressed={isMySavedPlacesActive ? "true" : "false"}
                onMouseEnter={() => setIsMSPHovered(true)} // Set hover to true
                onMouseLeave={() => setIsMSPHovered(false)} // Set hover to false
                onClick={clearsetSelectedVenueMsp}
                tabIndex={0}
                aria-label={
                  isMySavedPlacesActive
                    ? "My Saved Places Link Selected"
                    : "My Saved Places Link"
                }
                role="link"
              >
                <span className="w-6 h-6 relative">
                  {isDark ? (
                    <img
                      src={
                        isMySavedPlacesActive || isMSPHovered
                          ? "/images/Heart_dark_default.svg"
                          : "/images/Heart_white.svg"
                      }
                      alt="Saved Places Icon"
                      aria-hidden="true"
                    />
                  ) : (
                    <img
                      src={
                        isMySavedPlacesActive || isMSPHovered
                          ? "/images/Heart_active.svg"
                          : "/images/Heart_default.svg"
                      }
                      alt="Saved Places Icon"
                      aria-hidden="true"
                    />
                  )}
                </span>

                {isDark ? (
                  <span
                    aria-hidden="true"
                    className={`button-small-medium relative ${isMySavedPlacesActive || isMSPHovered ? "text-primary-300" : "text-white"}`}
                  >
                    {i18n.get("mySavedPlaces")}
                    {/* Render the div on hover */}
                    {isMSPHovered && (
                      <div
                        style={{
                          width: "100%", // Ensure the hover effect takes the full width of the text
                          height: 0,
                          borderBottom: "3px solid #A39FE8", // 3px border for light theme
                          position: "absolute", // Ensure the border is positioned without moving the text
                          bottom: -8, // Adjust position right under the text
                          left: 0, // Align with the text
                          marginTop: "4px",
                        }}
                      ></div>
                    )}
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    className={`button-small-medium relative ${isMySavedPlacesActive || isMSPHovered ? "text-primary-500" : "text-neutral-700"}`}
                  >
                    {i18n.get("mySavedPlaces")}
                    {/* Render the div on hover */}
                    {isMSPHovered && (
                      <div
                        style={{
                          width: "100%", // Ensure the hover effect takes the full width of the text
                          height: 0,
                          borderBottom: "3px solid #352FC3", // 3px border for dark theme
                          position: "absolute", // Ensure the border is positioned without moving the text
                          bottom: -8, // Adjust position right under the text
                          left: 0, // Align with the text
                          marginTop: "4px",
                        }}
                      ></div>
                    )}
                  </span>
                )}
              </div>
            </li>
          </ul>

          {!user?.name && (
            <div className="flex items-center pl-9 pt-1">
              {/* Login Button */}
              <button
                className={cn(
                  "hidden md:flex items-center py-[10px] h-[40px] rounded-[12px] px-6 bg-primary-800 dark:bg-[#4640BE] border border-primary-800 dark:border-[#4640BE] cursor-pointer"
                )}
                aria-label={`${i18n.get("loginNavbar")}`}
                onClick={() => {
                  handleOpenAuthModal();
                }}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "button-small-semi-bold hidden md:block text-primary-800 dark:text-primary-50 flex-row truncate text-primary-50"
                  )}
                >
                  {i18n.get("loginNavbar")}
                </span>
              </button>
              {/* Profile Button */}
              <button
                tabIndex={0}
                className="ml-6 md:flex items-center gap-1 cursor-pointer relative"
                onClick={toggleDrawerMenu}
                aria-label="Profile menu"
              >
                <img
                  className="w-12 h-12 rounded-full bg-neutral-300"
                  src="/images/user_placeholder.svg"
                  alt="Default profile icon"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
          {user?.name && (
            <div className="flex items-center pl-5 pt-1">
              <button
                tabIndex={0}
                className="ml-6 md:flex items-center gap-1 cursor-pointer relative"
                onClick={toggleDrawerMenu}
                aria-label="Profile menu"
              >
                {(user as any)?.profile_photo ? (
                  <img
                    className="w-12 h-12 rounded-full"
                    src={profileUrl}
                    alt={`Profile picture of ${user?.name}`}
                    aria-hidden="true"
                  />
                ) : (
                  <NameInitialsAvatar
                    name={user?.name?.split(" ")[0]}
                    bgColor={
                      user?.profile_color_code
                        ? user.profile_color_code
                        : "#8751FF"
                    }
                    textColor="white"
                    textSize="20px"
                    size="48px"
                    borderColor={
                      user?.profile_color_code
                        ? user.profile_color_code
                        : "#8751FF"
                    }
                    aria-label={`Profile initials of ${user?.name}`}
                  />
                )}
              </button>
            </div>
          )}
        </nav>
      </header>
      <div className="relative z-[1020]">
        <HamburgerMenu />
      </div>
    </div>
  );
}

export default Navbar;
